import { isNumber } from 'lodash';
import { useRef } from 'react';
import { useFramework, useScale } from '@/framework';
import { getLayerInfoWithRotate, sampleLayersUniformly } from '@/utils/layer-kit';
import { type ILayerProps, type IPoint, type ISize } from '@/version-types';
import { SmartGuidesPlugin } from '../../smart-guides';
import { MOVING_ABSORB_DISTANCE } from '../constants';
import BlockList from '../tools/block-list';
import LayerBlock from '../tools/layer-block';
import LineList from '../tools/line-list';

const outOfRange = (n: number, range: number[]) => {
    return n <= range[0] || n >= range[1];
};

export const useMoveSnap = () => {
    const framework = useFramework();
    const scale = useScale();
    const scaleRef = useRef(scale);
    scaleRef.current = scale;

    const containerSize = useRef<ISize>({ width: 0, height: 0 });

    // x 轴吸附
    const isAdsorbX = useRef<boolean>(false);
    // x 轴吸附范围
    const adsorbRangeX = useRef<number[]>([0, 0]);
    // y 轴吸附
    const isAdsorbY = useRef<boolean>(false);
    // y 轴吸附范围
    const adsorbRangeY = useRef<number[]>([0, 0]);
    // 吸附状态下的位移
    const adsorbDisplacement = useRef<IPoint>({ x: 0, y: 0 });
    // 间距 x 轴吸附
    const isBlockAdsorbX = useRef<boolean>(false);
    // 间距 y 轴吸附
    const isBlockAdsorbY = useRef<boolean>(false);

    const lineData = useRef<LineList>();
    const blockData = useRef<BlockList>();
    const layerBlock = useRef<LayerBlock>();
    const layers = useRef<ILayerProps[]>([]);
    const currentGuideData = useRef<number>();

    const proxyBeforeMoving = (layer?: ILayerProps | null) => {
        if (!layer) return;
        const originIndex = layer.__stickyFrameId ?? layer.__originIndex ?? framework.service.frame.getActiveFrameId();

        if (framework.service.edgelessCanvas.isEdgelessMode) {
            if (isNumber(currentGuideData.current) && originIndex === currentGuideData.current) {
                return;
            }
        }

        // console.log('===proxyBeforeMoving');

        currentGuideData.current = originIndex;

        lineData.current = new LineList();
        blockData.current = new BlockList();
        const activeFrameSize = framework.service.frame.getActiveFrameSize();
        containerSize.current = activeFrameSize;

        if (!framework.service.edgelessCanvas.isEdgelessMode) {
            // 舞台边缘
            lineData.current.push({
                width: activeFrameSize.width,
                height: activeFrameSize.height,
                left: 0,
                top: 0,
                rotate: 0,
                isStage: true,
                id: 'stage_' + Date.now(),
            });
        }
        // 元素
        if (layer.parentId) {
            const warpLayer = framework.service.flatLayer.getWrapLayer(layer.id);
            if (warpLayer) {
                layers.current = framework.service.flatLayer.getChildrenLayers(warpLayer.id);
            }
        } else {
            const originIndex = currentGuideData.current;
            // layer.__stickyFrameId ?? layer.__originIndex ?? framework.service.frame.getActiveFrameId();
            const activeLayerIds = framework.service.frame.getActiveLayerIds();
            const frame = framework.service.frame.get(originIndex);
            const transform = framework.service.frame.getFrameTransform(originIndex);

            if (frame) {
                containerSize.current = frame;
                // 舞台边缘
                lineData.current.push({
                    width: frame.width,
                    height: frame.height,
                    left: frame.left,
                    top: frame.top - transform.offsetTop,
                    rotate: 0,
                    isStage: true,
                    id: 'stage_' + Date.now(),
                });
            }
            layers.current = framework.service.frame.getFlatLayers(originIndex, activeLayerIds);
        }

        layers.current = layers.current.filter((layer) => {
            return (
                framework.service.edgelessCanvas.getByLayerId(layer.id)?.checkInViewport().intersects &&
                !framework.service.flatLayer.checkIsNeedHidden(layer)
            );
        });

        sampleLayersUniformly(layers.current, layer, false).forEach((item) => {
            lineData.current!.push(item);
            if (layer) {
                blockData.current!.push(item, layers.current, layer, containerSize.current);
            }
        });
    };
    const proxyMoving = (
        displacementX: number,
        displacementY: number,
        oldLayerRect: ILayerProps,
        layer?: ILayerProps | null,
    ) => {
        if (!layer) return { x: 0, y: 0, isSnapX: false, isSnapY: false };

        const lineNearest = lineData.current?.getNearest(layer);
        layerBlock.current = new LayerBlock(layer.id);
        const blockNearest = layerBlock.current!.getNearest(layer, blockData.current);

        // 转换吸附距离到图层
        const activeLayerInfo = getLayerInfoWithRotate(oldLayerRect);
        const adsorbDistance = Math.max(1, Math.ceil(MOVING_ABSORB_DISTANCE / scaleRef.current));

        if (lineNearest || blockNearest) {
            if (lineNearest) {
                if (!isBlockAdsorbX.current) {
                    // x 轴吸附位移
                    const nearestDisplacementX = lineNearest.vertical.position - activeLayerInfo.left;
                    // 进入吸附范围
                    if (Math.abs(lineNearest.vertical.distance) < adsorbDistance) {
                        if (!isAdsorbX.current) {
                            adsorbDisplacement.current.x = nearestDisplacementX;
                            // 移动吸附范围
                            adsorbRangeX.current = [
                                nearestDisplacementX - adsorbDistance,
                                nearestDisplacementX + adsorbDistance,
                            ];
                            isAdsorbX.current = true;
                        }
                    }
                    if (isAdsorbX.current) {
                        // 超出吸附范围, 停止吸附
                        if (outOfRange(displacementX, adsorbRangeX.current)) {
                            isAdsorbX.current = false;
                            adsorbDisplacement.current.x = displacementX;
                        }
                    } else {
                        adsorbDisplacement.current.x = displacementX;
                    }
                }
                if (!isBlockAdsorbY.current) {
                    // y 轴吸附位移
                    const nearestDisplacementY = lineNearest.horizontal.position - activeLayerInfo.top;
                    // 进入吸附范围
                    if (Math.abs(lineNearest.horizontal.distance) < adsorbDistance) {
                        if (!isAdsorbY.current) {
                            adsorbDisplacement.current.y = nearestDisplacementY;
                            // 吸附范围
                            adsorbRangeY.current = [
                                nearestDisplacementY - adsorbDistance,
                                nearestDisplacementY + adsorbDistance,
                            ];
                            isAdsorbY.current = true;
                        }
                    }
                    if (isAdsorbY.current) {
                        // 超出吸附范围, 停止吸附
                        if (outOfRange(displacementY, adsorbRangeY.current)) {
                            isAdsorbY.current = false;
                            adsorbDisplacement.current.y = displacementY;
                        }
                    } else {
                        adsorbDisplacement.current.y = displacementY;
                    }
                }
            }
            if (blockNearest) {
                if (!isAdsorbX.current) {
                    // 计算 x 轴吸附
                    const nearestDisplacementX = blockNearest.vertical.position - activeLayerInfo.left;
                    if (Math.abs(blockNearest.vertical.distance) < adsorbDistance) {
                        if (!isBlockAdsorbX.current) {
                            adsorbDisplacement.current.x = nearestDisplacementX;
                            adsorbRangeX.current = [
                                nearestDisplacementX - adsorbDistance,
                                nearestDisplacementX + adsorbDistance,
                            ];
                            isBlockAdsorbX.current = true;
                        }
                    }
                    if (isBlockAdsorbX.current) {
                        if (outOfRange(displacementX, adsorbRangeX.current)) {
                            isBlockAdsorbX.current = false;
                            adsorbDisplacement.current.x = displacementX;
                        }
                    } else {
                        adsorbDisplacement.current.x = displacementX;
                    }
                }
                if (!isAdsorbY.current) {
                    // 计算 y 轴吸附
                    const nearestDisplacementY = blockNearest.horizontal.position - activeLayerInfo.top;
                    if (Math.abs(blockNearest.horizontal.distance) < adsorbDistance) {
                        if (!isBlockAdsorbY.current) {
                            adsorbDisplacement.current.y = nearestDisplacementY;
                            adsorbRangeY.current = [
                                nearestDisplacementY - adsorbDistance,
                                nearestDisplacementY + adsorbDistance,
                            ];
                            isBlockAdsorbY.current = true;
                        }
                    }
                    if (isBlockAdsorbY.current) {
                        if (outOfRange(displacementY, adsorbRangeY.current)) {
                            isBlockAdsorbY.current = false;
                            adsorbDisplacement.current.y = displacementY;
                        }
                    } else {
                        adsorbDisplacement.current.y = displacementY;
                    }
                }
            }
        } else {
            adsorbDisplacement.current.x = displacementX;
            adsorbDisplacement.current.y = displacementY;
        }

        return {
            ...adsorbDisplacement.current,
            isSnapX: isAdsorbX.current || isBlockAdsorbX.current,
            isSnapY: isAdsorbY.current || isBlockAdsorbY.current,
        };
    };

    const resetMoveSnap = () => {
        lineData.current = undefined;
        blockData.current = undefined;
        layerBlock.current = undefined;
        layers.current = [];
        containerSize.current = { width: 0, height: 0 };
        isAdsorbX.current = false;
        isAdsorbY.current = false;
        isBlockAdsorbX.current = false;
        isBlockAdsorbY.current = false;
        currentGuideData.current = undefined;
    };

    const proxyAfterMoving = (layer: ILayerProps | null | undefined, isEnd: boolean) => {
        if (layer) {
            // 对齐 UI 展示
            const lines = lineData.current?.getLines(layer);
            if (lines) {
                SmartGuidesPlugin.get(framework)?.setLines(lines);
            }

            // 等间距 UI 展示
            if (layerBlock.current) {
                layerBlock.current.collectBlocks(layer, layers.current, layer, containerSize.current);
                const blocks = layerBlock.current.getBlocks(blockData.current);
                if (blocks) {
                    SmartGuidesPlugin.get(framework)?.setBlocks(blocks);
                }
            }
        }

        // 清除临时数据
        if (isEnd) {
            resetMoveSnap();
        }
    };

    return {
        proxyMoving,
        proxyBeforeMoving,
        proxyAfterMoving,
        resetMoveSnap,
    };
};
